import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SettingsService } from '~billing/services/settings.service';
import { Frac } from '~lmo/models/frac.model';
import {
  FracClusterPurchaseOrderRequest,
  PurchaseOrderPermittedFrac,
  PurchaseOrderUpdate,
} from '~lmo/models/purchaseOrders.model';
import { milePluralMapping } from '~lmo/pluralMappings';
import { LmoFracsService } from '~lmo/services';
import { LmoFracFcpoService } from '~lmo/services/lmo-frac-fcpo.service';
import { LmoFracMineService } from '~lmo/services/lmo-frac-mine.service';
import { UserService } from '~services/user.service';

@Component({
  selector: 'sa-lmo-purchase-order-add-supported-well',
  styleUrls: ['./lmo-purchase-order-add-supported-well.component.scss'],
  templateUrl: './lmo-purchase-order-add-supported-well.component.html',
})
export class LmoPurchaseOrderAddSupportedWellComponent implements OnInit {
  public isPOLineItemNumberMandatory$ = new BehaviorSubject<boolean>(false);
  public isSettingLoaded$ = new BehaviorSubject<boolean>(false);
  public fracList$: Observable<Frac[]> = new Observable<Frac[]>();
  public isFracListSelectionEmpty = false;

  public supportedWellFormGroup: FormGroup;
  public billableMileage$: Observable<number>;
  public milePluralMapping = milePluralMapping;
  public mineId$ = new BehaviorSubject<number>(null);
  public purchaseOrderUpdate$ = new BehaviorSubject<PurchaseOrderUpdate>(null);
  @Output() public successEvent = new EventEmitter<boolean>();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      mineId: number;
      purchaseOrderUpdate: PurchaseOrderUpdate;
      meshId: number;
      permittedFracs: PurchaseOrderPermittedFrac[];
      defaultPOLineItemNumber: string;
    },
    public dialogRef: MatDialogRef<LmoPurchaseOrderAddSupportedWellComponent>,
    private snackBar: MatSnackBar,
    private settingService: SettingsService,
    private userService: UserService,
    private fracService: LmoFracsService,
    private fracMineService: LmoFracMineService,
    private fcpoService: LmoFracFcpoService,
    private fb: FormBuilder,
  ) {
    this.setupSupportedWellSelection(data);
  }

  public ngOnInit(): void {
    this.mineId$.next(this.data.mineId);
    this.purchaseOrderUpdate$.next(this.data.purchaseOrderUpdate);
    this.settingService.loadSettings(this.userService.accountId()).subscribe((settings) => {
      this.isPOLineItemNumberMandatory$.next(settings.isPOLineItemNumberMandatory);
      this.isSettingLoaded$.next(true);
      this.setupForm(settings.isPOLineItemNumberMandatory);
    });
  }

  private setupForm(isPOLineItemNumberMandatory: boolean) {
    this.supportedWellFormGroup = this.fb.group({
      billableMileage: [0],
      defaultLoadWeight: [null, [Validators.min(10000), Validators.max(42000)]],
      fracs: [null, [Validators.required, Validators.min(0)]],
      poLineItemNumber: [
        isPOLineItemNumberMandatory ? this.data.defaultPOLineItemNumber : null,
        isPOLineItemNumberMandatory ? [Validators.required] : [],
      ],
    });
  }

  public async addSupportedWell() {
    if (
      this.supportedWellFormGroup.invalid ||
      (this.supportedWellFormGroup.get('poLineItemNumber').value &&
        !this.supportedWellFormGroup.get('poLineItemNumber').value.trim() &&
        this.isPOLineItemNumberMandatory$.value)
    ) {
      return;
    }

    const fracId = this.supportedWellFormGroup.get('fracs').value;
    const billableMileage = this.supportedWellFormGroup.get('billableMileage').value;
    const poLineItemNumber =
      this.supportedWellFormGroup.get('poLineItemNumber').value &&
      this.supportedWellFormGroup.get('poLineItemNumber').value.trim()
        ? this.supportedWellFormGroup.get('poLineItemNumber').value.trim()
        : null;

    const success = await this.fracMineService
      .setFracMineRouteFromPODetail$(this.mineId$.value, fracId, billableMileage, null)
      .toPromise();
    if (!success) {
      this.snackBar.open('Failed to add supported well', null, { duration: 3000, panelClass: ['snackbar-error'] });
      return;
    }
    let fracClusterId = 0;
    this.fracService
      .getFracClusterIdByFracId$(fracId)
      .pipe(
        map((number) => {
          return number;
        }),
      )
      .subscribe((clusterId) => {
        fracClusterId = clusterId;
      });

    const body: FracClusterPurchaseOrderRequest = {
      fracClusterId: fracClusterId,
      poLineItemNumber: poLineItemNumber,
      preferredOrder: null,
      purchaseOrder: this.purchaseOrderUpdate$.value,
    };
    this.fcpoService.addFcpo$(body).subscribe((successful) => {
      if (successful) {
        this.successEvent.emit(true);
        this.dialogRef.close();
      } else {
        this.successEvent.emit(false);
        this.snackBar.open('Failed to add supported well', null, { duration: 3000, panelClass: ['snackbar-error'] });
      }
    });
  }

  private setupSupportedWellSelection(data: LmoPurchaseOrderAddSupportedWellComponent['data']) {
    const listOfFrac$ = this.fracService.fracs$.pipe(
      map((fracs) => fracs.filter((frac) => this.hasSelectedMeshId(frac, data.meshId))),
    );
    if (!data.permittedFracs?.length) {
      this.fracList$ = listOfFrac$;
    } else {
      this.fracList$ = listOfFrac$.pipe(
        map((fracs) =>
          fracs.filter((frac) => data.permittedFracs.some((permittedFrac) => permittedFrac.id === frac.id)),
        ),
      );
      this.fracList$.subscribe((fracs) => {
        this.isFracListSelectionEmpty = fracs.length === 0;
      });
    }
  }

  private hasSelectedMeshId(frac: Frac, meshId: number): boolean {
    return frac.fracStorages.some((storage) => storage.mesh.id === meshId);
  }

  public closeDialog() {
    this.dialogRef.close();
  }
}
